import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Menu, X, Shield, ChevronRight } from 'lucide-react';

const NAV_LINKS = [
  { label: 'Features',     href: '#features'    },
  { label: 'How it works', href: '#how-it-works' },
  { label: 'Pricing',      href: '#pricing'     },
  { label: 'Docs',         href: '/docs'        },
];

const Navbar = () => {
  const [open,     setOpen]     = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleLogout = () => { logout(); navigate('/'); setOpen(false); };

  const scrollTo = (href) => {
    setOpen(false);
    if (href.startsWith('#') && href.length > 1) {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <header
      className={[
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
        scrolled
          ? 'bg-[#080c16]/95 backdrop-blur-xl border-b border-white/[0.06] shadow-lg shadow-black/20'
          : 'bg-transparent',
      ].join(' ')}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between gap-6">

        {/* Logo */}
        <Link to="/" className="flex items-center gap-2.5 flex-shrink-0 group">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#00E5FF] to-[#7C3AED] flex items-center justify-center shadow-lg group-hover:shadow-[0_0_16px_rgba(0,229,255,0.4)] transition-shadow">
            <Shield size={15} className="text-white" />
          </div>
          <span className="text-white font-bold text-base tracking-tight">
            Zenith<span className="text-[#00E5FF]">Cipher</span>
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-0.5">
          {NAV_LINKS.map(({ label, href }) =>
            href.startsWith('/') ? (
              <Link
                key={label}
                to={href}
                className="px-4 py-2 text-sm text-slate-400 hover:text-white rounded-lg hover:bg-white/5 transition-all"
              >
                {label}
              </Link>
            ) : (
              <button
                key={label}
                onClick={() => scrollTo(href)}
                className="px-4 py-2 text-sm text-slate-400 hover:text-white rounded-lg hover:bg-white/5 transition-all"
              >
                {label}
              </button>
            )
          )}
        </nav>

        {/* Desktop CTA */}
        <div className="hidden lg:flex items-center gap-2">
          {user ? (
            <>
              <Link
                to="/dashboard"
                className="flex items-center gap-1.5 text-sm text-slate-300 hover:text-white px-4 py-2 rounded-lg hover:bg-white/5 transition-all"
              >
                Dashboard <ChevronRight size={13} className="text-[#00E5FF]" />
              </Link>
              <button
                onClick={handleLogout}
                className="text-sm text-slate-400 hover:text-slate-200 px-4 py-2 rounded-lg border border-white/10 hover:border-white/20 transition-all"
              >
                Sign out
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                className="text-sm text-slate-300 hover:text-white px-4 py-2 rounded-lg hover:bg-white/5 transition-all"
              >
                Sign in
              </Link>
              <Link
                to="/register"
                className="flex items-center gap-1.5 text-sm font-semibold bg-[#00E5FF] hover:bg-[#00cce5] text-black px-4 py-2 rounded-lg transition-all shadow-lg shadow-[#00E5FF]/20 hover:shadow-[#00E5FF]/40"
              >
                Start Free
                <ChevronRight size={13} />
              </Link>
            </>
          )}
        </div>

        {/* Mobile hamburger */}
        <button
          className="lg:hidden text-slate-400 hover:text-white p-1.5 rounded-lg hover:bg-white/5 transition-all"
          onClick={() => setOpen(v => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-[#080c16]/98 backdrop-blur-xl border-t border-white/[0.06] px-5 py-4 space-y-1">
          {NAV_LINKS.map(({ label, href }) =>
            href.startsWith('/') ? (
              <Link
                key={label}
                to={href}
                onClick={() => setOpen(false)}
                className="block px-3 py-2.5 text-sm text-slate-400 hover:text-white rounded-lg hover:bg-white/5 transition-all"
              >
                {label}
              </Link>
            ) : (
              <button
                key={label}
                onClick={() => scrollTo(href)}
                className="w-full text-left px-3 py-2.5 text-sm text-slate-400 hover:text-white rounded-lg hover:bg-white/5 transition-all"
              >
                {label}
              </button>
            )
          )}
          <div className="pt-3 mt-2 border-t border-white/[0.06] flex flex-col gap-2">
            {user ? (
              <>
                <Link
                  to="/dashboard"
                  onClick={() => setOpen(false)}
                  className="px-3 py-2.5 text-sm text-[#00E5FF] font-medium rounded-lg bg-[#00E5FF]/10 text-center"
                >
                  Go to Dashboard
                </Link>
                <button
                  onClick={handleLogout}
                  className="px-3 py-2.5 text-sm text-slate-400 rounded-lg border border-white/10 text-center"
                >
                  Sign out
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/register"
                  onClick={() => setOpen(false)}
                  className="px-3 py-2.5 text-sm font-semibold bg-[#00E5FF] text-black rounded-lg text-center"
                >
                  Start Free
                </Link>
                <Link
                  to="/login"
                  onClick={() => setOpen(false)}
                  className="px-3 py-2.5 text-sm text-slate-400 rounded-lg border border-white/10 text-center"
                >
                  Sign in
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
